import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Plus,
  X,
  Check,
  Upload,
  Crop,
  User,
  Building2,
  Phone,
  Mail,
  ChevronRight,
  Star,
  IdCard,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import imageOne from "../../../public/placeholder.svg";
import imageTwo from "../../../public/placeholder.svg";
import photo from "../../assets/hero-properties.jpg";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Copied
// verbatim from the property templates' own getEditableImageUrl helper.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

// ─── Helper: convert the crop modal's output data URL into a File, the same
// way a freshly-picked hero photo already becomes a File.
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

interface ServiceData {
  id?: string | number;
  title?: string;
  serviceType?: string;
  subtitle?: string;
  tagline?: string;
  description?: string;
  features?: string[] | { feature_name?: string; id?: number }[];
  phone?: string;
  address?: string;
  website?: string;
  email?: string;
  heroImage?: string;
  hero_image?: string;
  hero_image_url?: string;
  // Untouched original hero photo, kept separately so Edit Image can always
  // re-crop from the full source instead of a previous crop.
  original_hero_image?: string;
  image?: string;
  aboutImage?: string;
  photos?: string[];
  profileImage?: string;
  companyImage?: string;
  userAvatar?: string;
  userCompanyLogo?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  agentAvatar?: string;
  company?: string;
  companyLogo?: string;
  aboutTitle?: string;
  servicesTitle?: string;
  copyrightText?: string;
  companyName?: string;
}

interface UserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
  license_number?: string;
}

interface ServiceTemplate2Props {
  serviceData?: ServiceData;
  data?: ServiceData;
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Record<string, any>) => void;
  readOnly?: boolean;
  isPreview?: boolean;
}

const MAX_FEATURES = 10;

const DEFAULTS = {
  title: "General Contractor",
  subtitle: "Best From World",
  tagline: "",
  description:
    "We are a full-service general contracting company committed to delivering high-quality construction and renovation projects. With a focus on craftsmanship, reliability, and clear communication, we manage every phase of the build from planning to completion.",
  features: [
    { id: 1, feature_name: "RESIDENTIAL & COMMERCIAL CONSTRUCTION" },
    { id: 2, feature_name: "RENOVATIONS & REMODELING" },
    { id: 3, feature_name: "REPAIRS & MAINTENANCE" },
  ],
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  address: "123 Main Street, City, State 12345",
  website: "www.yourwebsite.com",
  aboutTitle: "ABOUT US",
  servicesTitle: "OUR SERVICES",
  copyrightText: "All rights reserved.",
  companyName: "General Contractor",
};

const normalizeFeatures = (
  features: ServiceData["features"]
): { id?: number; feature_name: string }[] => {
  if (!features || !Array.isArray(features)) return [];
  return features
    .map((f, index) => {
      if (typeof f === "string") return { id: index, feature_name: f.trim() };
      if (typeof f === "object" && f !== null)
        return { id: f.id ?? index, feature_name: (f.feature_name || "").trim() };
      return { id: index, feature_name: "" };
    })
    .filter((f) => f.feature_name.length > 0);
};

type FeatureRow = {
  id: string;
  feature_name: string;
};

const createFeatureId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const areFeatureRowsEqual = (left: FeatureRow[], right: { feature_name: string }[]) =>
  left.length === right.length &&
  left.every((item, index) => item.feature_name.trim() === right[index]?.feature_name.trim());

const mapFeatureRows = (
  features: ServiceData["features"],
  previous: FeatureRow[] = []
): FeatureRow[] => {
  const normalized = normalizeFeatures(features).map((feature) => ({
    feature_name: feature.feature_name,
  }));

  if (normalized.length === 0) return previous;
  if (areFeatureRowsEqual(previous, normalized)) return previous;

  const mapped = normalized.map((feature, index) => ({
    id: previous[index]?.id ?? createFeatureId(),
    feature_name: feature.feature_name,
  }));

  const trailingEmptyRows = previous
    .slice(normalized.length)
    .filter((row) => !row.feature_name.trim());

  return [...mapped, ...trailingEmptyRows];
};

const mapDefaultFeatureRows = (): FeatureRow[] =>
  DEFAULTS.features.map((feature) => ({
    id: `default-${feature.id}`,
    feature_name: feature.feature_name,
  }));

// ─── EditableText ────────────────────────────────────────────────────────────
const EditableText = ({
  value,
  defaultValue,
  onChange,
  readOnly,
  className = "",
  as = "span",
  multiline = false,
  placeholder = "Click to edit...",
  uppercase = false,
}: {
  value?: string;
  defaultValue: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  className?: string;
  as?: "span" | "h1" | "h2" | "h3" | "p";
  multiline?: boolean;
  placeholder?: string;
  uppercase?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => { setTempValue(value || ""); }, [value]);
  useEffect(() => { if (isEditing && inputRef.current) inputRef.current.focus(); }, [isEditing]);

  const displayValue = value?.trim() || defaultValue;
  const finalDisplayValue = uppercase ? displayValue.toUpperCase() : displayValue;

  const handleSave = () => { onChange(tempValue); setIsEditing(false); };
  const handleCancel = () => { setTempValue(value || ""); setIsEditing(false); };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) handleSave();
    if (e.key === "Escape") handleCancel();
  };

  if (readOnly) {
    const Tag = as;
    return <Tag className={className}>{finalDisplayValue}</Tag>;
  }

  if (isEditing) {
    return (
      <div className="inline-flex items-start gap-2 w-full">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full bg-white/20 backdrop-blur-sm border border-white/30 rounded px-2 py-1 text-inherit focus:outline-none focus:ring-2 focus:ring-white/50 resize-none"
            rows={3}
            placeholder={placeholder}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full bg-white/20 backdrop-blur-sm border border-white/30 rounded px-2 py-1 text-inherit focus:outline-none focus:ring-2 focus:ring-white/50"
            placeholder={placeholder}
          />
        )}
        <button onClick={handleSave} className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex-shrink-0 mt-0.5">
          <Check className="w-4 h-4" />
        </button>
        <button onClick={handleCancel} className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex-shrink-0 mt-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const Tag = as;
  return (
    <div className={`group relative cursor-pointer inline-block max-w-full ${className}`} onClick={() => setIsEditing(true)}>
      <Tag className={`${className} border-b-2 border-dashed border-transparent group-hover:border-current transition-all break-words`}>
        {finalDisplayValue}
      </Tag>
      <Pencil className="absolute -right-6 top-1/2 -translate-y-1/2 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};

// ─── EditableImage ────────────────────────────────────────────────────────────
const EditableImage = ({
  src,
  defaultSrc,
  alt,
  onChange,
  onUploaded,
  onLoaded,
  readOnly,
  className = "",
  fallbackIcon,
  backgroundClassName,
}: {
  src?: string;
  defaultSrc: string;
  alt: string;
  onChange: (value: string) => void;
  onUploaded?: (file: File, dataUrl: string) => void;
  onLoaded?: () => void;
  readOnly: boolean;
  className?: string;
  fallbackIcon?: React.ReactNode;
  backgroundClassName?: string;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      onChange(result);
      onUploaded?.(file, result);

      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
        onLoaded?.();
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const imageSrc = src || defaultSrc;
  const hasValidImage = imageSrc && imageSrc !== defaultSrc && !imageSrc.includes("placeholder.svg");

  const renderContent = () => (
    <div className={`flex items-center justify-center w-full h-full ${backgroundClassName ?? "bg-gradient-to-br from-stone-100 to-stone-200"} overflow-hidden`}>
      {hasValidImage ? (
        <img
          src={imageSrc}
          alt={alt}
          className="w-full h-full object-cover"
          onLoad={() => {
            requestAnimationFrame(() => {
              window.dispatchEvent(new Event("resize"));
              onLoaded?.();
            });
          }}
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full">
          {fallbackIcon || <img src={imageSrc} alt={alt} className="w-full h-full object-cover opacity-30" />}
        </div>
      )}
    </div>
  );

  if (readOnly) return <div className={className}>{renderContent()}</div>;

  return (
    <div className={`relative group ${className}`}>
      {renderContent()}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1.5 bg-white text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-100 transition-colors text-sm"
        >
          <Upload className="w-3 h-3" />
          {hasValidImage ? "Change" : "Upload"}
        </button>
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ServiceTemplate2 = ({
  serviceData: propServiceData,
  data: propData,
  onDirtyChange,
  onChange,
  readOnly = true,
  isPreview = false,
}: ServiceTemplate2Props) => {
  const serviceData = propServiceData || propData || {};
  const { toast } = useToast();

  const [localServiceData, setLocalServiceData] = useState<ServiceData>({});
  const [userData, setUserData] = useState<UserData>({});
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState<FeatureRow[]>([]);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>(null);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const heroContainerRef = useRef<HTMLDivElement | null>(null);

  const measureHeroResolution = useCallback(() => {
    if (!heroContainerRef.current) return;

    const rect = heroContainerRef.current.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);

    setBoxHeroRes(`${width}x${height}`);
  }, []);

  const notifyHeroResolution = useCallback(
    (file: File, dataUrl: string) => {
      if (!boxHeroRes) return;
      const img = new Image();
      img.onload = () => {
        const parts = boxHeroRes.split("x").map((s) => Number(s.trim()));
        if (parts.length === 2) {
          toast({
            title: "Resolution Suggestion",
            description: `Uploaded image is ${img.naturalWidth}x${img.naturalHeight}. Recommended: ${boxHeroRes}. If possible, upload this resolution for best results.`,
            duration: 4000,
          });
        }
      };
      img.src = dataUrl;
    },
    [boxHeroRes, toast]
  );

  useEffect(() => {
    measureHeroResolution();
    const rafId = window.requestAnimationFrame(measureHeroResolution);
    window.addEventListener("resize", measureHeroResolution);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && heroContainerRef.current) {
      resizeObserver = new ResizeObserver(measureHeroResolution);
      resizeObserver.observe(heroContainerRef.current);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measureHeroResolution);
      resizeObserver?.disconnect();
    };
  }, [measureHeroResolution]);

  const prevIdRef = useRef<string | number | undefined>();

  useEffect(() => {
    if (!serviceData) return;
    const idChanged = prevIdRef.current !== serviceData.id;
    prevIdRef.current = serviceData.id;

    if (idChanged) {
      setLocalServiceData({ ...serviceData });
      setFeatures(mapFeatureRows(serviceData.features, []));
    } else {
      setLocalServiceData((prev) => ({ ...prev, ...serviceData }));
    }
  }, [serviceData]);

  useEffect(() => {
    setFeatures((prev) => {
      const mapped = mapFeatureRows(serviceData?.features || localServiceData.features, prev);
      if (mapped.length > 0) return mapped;
      return prev.length === 0 ? mapDefaultFeatureRows() : prev;
    });
  }, [serviceData?.features, localServiceData.features]);

  useEffect(() => {
    let isMounted = true;
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(endpoints.me);
        const data = response.data;
        if (!isMounted) return;
        setUserData(data);
        setLocalServiceData((prev) => ({
          ...prev,
          userAvatar: prev.userAvatar || prev.profileImage || data.avatar || "",
          userCompanyLogo: prev.userCompanyLogo || prev.companyImage || data.company_logo || "",
          agentName: prev.agentName || `${data.first_name || ""} ${data.last_name || ""}`.trim(),
          agentPhone: prev.agentPhone || data.phone_number || "",
          agentEmail: prev.agentEmail || data.email || "",
          company: prev.company || prev.companyName || data.company || "",
        }));
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchUserData();
    return () => { isMounted = false; };
  }, []);

  const handleInputChange = useCallback((field: string, value: any) => {
    setLocalServiceData((prev) => {
      const next = { ...prev, [field]: value };
      onDirtyChange?.(true);
      onChange?.({ [field]: value });
      return next;
    });
  }, [onDirtyChange, onChange]);

  const handleHeroImageUpload = useCallback(
    async (file: File, dataUrl: string, options?: { captureOriginal?: boolean }) => {
      // captureOriginal defaults to true: a genuinely new photo pick becomes
      // its own untouched "original" so Edit Image can always re-crop from
      // the full source later. The Edit Image crop-confirm path reuses this
      // same handler and passes captureOriginal: false, so re-cropping an
      // already-cropped photo doesn't overwrite the true original.
      const captureOriginal = options?.captureOriginal ?? true;
      handleInputChange("heroImage", dataUrl);
      handleInputChange("hero_image_url", dataUrl);
      if (captureOriginal) setOriginalHeroImage(dataUrl);

      const serviceId = serviceData?.id || localServiceData.id;
      if (!serviceId) return;

      setIsSavingImage(true);
      const formData = new FormData();
      formData.append("hero_image", file);

      try {
        const response = await axiosInstance.patch(
          `${endpoints.service_listings}${serviceId}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const heroRaw =
          response.data?.hero_image_url ||
          response.data?.hero_image ||
          response.data?.image ||
          "";
        const heroFull = heroRaw
          ? heroRaw.startsWith("http")
            ? heroRaw
            : `${axiosInstance.defaults.baseURL || ""}${heroRaw.startsWith("/") ? "" : "/"}${heroRaw}`
          : dataUrl;

        handleInputChange("heroImage", heroFull);
        handleInputChange("hero_image_url", heroFull);

        if (captureOriginal) {
          setOriginalHeroImage(heroFull);
          axiosInstance
            .patch(`${endpoints.service_listings}${serviceId}/`, {
              original_hero_image: heroFull,
            })
            .catch((err) => {
              console.error("Failed to seed original hero image:", err);
            });
        }
      } catch (err) {
        console.error("[ServiceTemplate2] Failed to upload hero image:", err);
      } finally {
        setIsSavingImage(false);
      }
    },
    [handleInputChange, localServiceData.id, onChange, onDirtyChange, serviceData?.id]
  );

  const saveFieldToBackend = useCallback(
    async (field: string, value: any) => {
      const serviceId = serviceData?.id || localServiceData.id;
      if (!serviceId) return;

      try {
        const formData = new FormData();

        if (field === "features" && Array.isArray(value)) {
          const featureNames = value
            .map((f) => String(f?.feature_name ?? f ?? "").trim())
            .filter((f) => f.length > 0);
          formData.append("features", JSON.stringify(featureNames));
        } else {
          formData.append(field, String(value ?? ""));
        }

        const response = await axiosInstance.patch(
          `${endpoints.service_listings}${serviceId}/`,
          formData
        );

        const responseData = response.data || {};
        setLocalServiceData((prev) => ({ ...prev, ...responseData }));
        onChange?.(responseData);
      } catch (error) {
        console.error(`[ServiceTemplate2] Failed to save ${field}:`, error);
      }
    },
    [serviceData?.id, localServiceData.id, onChange]
  );

  const saveFeaturesToBackend = useCallback(
    async (featuresToSave: FeatureRow[]) => {
      await saveFieldToBackend("features", featuresToSave);
    },
    [saveFieldToBackend]
  );

  const getHeroImage = (): string => {
    const localHero =
      String(localServiceData.heroImage || localServiceData.hero_image_url || localServiceData.hero_image || localServiceData.image || "").trim();
    if (localHero) return localHero;

    const remoteHero =
      String(serviceData?.heroImage || serviceData?.hero_image_url || serviceData?.hero_image || serviceData?.image || "").trim();
    return remoteHero;
  };
  // FIXED - serviceData always wins over stale localServiceData
  const getValue = (field: keyof ServiceData): string => {
    const propValue = serviceData?.[field];
    if (typeof propValue === "string" && propValue.trim()) return propValue;
    const localValue = localServiceData[field];
    if (typeof localValue === "string" && localValue.trim()) return localValue;
    const defaultKey = field as keyof typeof DEFAULTS;
    if (DEFAULTS[defaultKey] && typeof DEFAULTS[defaultKey] === "string") return DEFAULTS[defaultKey] as string;
    return "";
  };
  const heroImageSrc = getHeroImage();

  // =========================================================================
  // EDIT IMAGE (crop/zoom the hero photo, non-destructively)
  // =========================================================================
  const EDIT_CROP_W = 333;
  const EDIT_CROP_H = 420; // proportional to the 515x650 hero box

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });
  const [originalHeroImage, setOriginalHeroImage] = useState<string>("");

  const openEditImage = async () => {
    if (readOnly || !heroImageSrc) return;

    let originalSource =
      originalHeroImage || localServiceData.original_hero_image || serviceData?.original_hero_image || "";

    const serviceId = serviceData?.id || localServiceData.id;

    if (!originalSource && serviceId) {
      try {
        const res = await axiosInstance.get(`${endpoints.service_listings}${serviceId}/`);
        originalSource = res?.data?.original_hero_image || "";
      } catch (err) {
        console.error("Failed to check for existing original hero image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal from the current image so this
      // and every future edit has a stable, full source to re-crop from
      // instead of the current display.
      originalSource = heroImageSrc;
      if (serviceId && !heroImageSrc.startsWith("data:")) {
        axiosInstance
          .patch(`${endpoints.service_listings}${serviceId}/`, {
            original_hero_image: heroImageSrc,
          })
          .catch((err) => {
            console.error("Failed to seed original hero image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    // Reuse the existing, unmodified hero upload handler exactly as it's
    // already wired to EditableImage's onUploaded, so the crop-confirm path
    // saves identically to a normal re-upload.
    const file = dataUrlToFile(croppedDataUrl, `edited-hero-${Date.now()}.png`);
    handleHeroImageUpload(file, croppedDataUrl, { captureOriginal: false });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  const addFeature = useCallback(() => {
    if (readOnly || features.length >= MAX_FEATURES) return;
    setFeatures((prev) => [...prev, { id: createFeatureId(), feature_name: "" }]);
    onDirtyChange?.(true);
  }, [readOnly, features.length, onDirtyChange]);

  const handleFeatureChange = useCallback((index: number, value: string) => {
    setFeatures((prev) => { const u = [...prev]; u[index] = { ...u[index], feature_name: value }; return u; });
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  const handleFeatureBlur = useCallback((index: number) => {
    const currentFeatures = features;
    const nextFeatures = [...currentFeatures];
    const featureNames = nextFeatures
      .map((f) => String(f?.feature_name ?? f ?? "").trim())
      .filter((f) => f.length > 0);
    setFeatures(nextFeatures);
    handleInputChange("features", featureNames);
    void saveFeaturesToBackend(nextFeatures);
  }, [features, handleInputChange, saveFeaturesToBackend]);

  const handleFeatureDelete = useCallback((index: number) => {
    if (features.length <= 1) return;
    const updated = features.filter((_, i) => i !== index);
    const featureNames = updated
      .map((f) => String(f?.feature_name ?? f ?? "").trim())
      .filter((f) => f.length > 0);
    setFeatures(updated);
    handleInputChange("features", featureNames);
    void saveFeaturesToBackend(updated);
  }, [features, handleInputChange, saveFeaturesToBackend]);

  // ── Computed display values ──
  const title = getValue("title");
  const subtitle = getValue("subtitle");
  const tagline = getValue("tagline");
  const description = getValue("description");
  const aboutTitle = getValue("aboutTitle");
  const serviceType = serviceData?.serviceType || localServiceData.serviceType || "";

  const agentFullName =
    localServiceData.agentName ||
    serviceData?.agentName ||
    (userData?.first_name ? `${userData.first_name} ${userData.last_name || ""}`.trim() : "") ||
    "Your Name";

  const displayPhone = userData?.phone_number || localServiceData.agentPhone || serviceData?.agentPhone || localServiceData.phone || serviceData?.phone || DEFAULTS.phone;
  const displayEmail = userData?.email || localServiceData.agentEmail || serviceData?.agentEmail || localServiceData.email || serviceData?.email || DEFAULTS.email;
  const displayCompanyName = userData?.company || localServiceData.company || localServiceData.companyName || serviceData?.company || serviceData?.companyName || DEFAULTS.companyName;

  const displayAvatar = localServiceData.profileImage || localServiceData.userAvatar || serviceData?.profileImage || serviceData?.agentAvatar || userData?.avatar || "";
  const displayCompanyLogo = localServiceData.companyImage || localServiceData.userCompanyLogo || serviceData?.companyImage || serviceData?.companyLogo || userData?.company_logo || "";

  if (loading && !isPreview) {
    return (
      <div className="max-w-6xl mx-auto bg-stone-50 shadow-2xl rounded-2xl p-12 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-stone-900 border-r-transparent mb-4" />
          <p className="text-stone-600">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto w-full min-w-0" style={{ WebkitOverflowScrolling: "touch", transform: "rotateX(180deg)" }}>
      <div
        className={`${readOnly ? "bg-white block w-full" : "bg-stone-50"} shadow-none rounded-2xl overflow-hidden w-full max-w-none mx-auto relative ${!readOnly ? "ring-2 ring-stone-900 ring-offset-4 inline-block align-top" : ""}`}
        style={{ ...(readOnly ? { width: "100%" } : {}), minWidth: "835px", transform: "rotateX(180deg)" }}
      >
        {/* Edit Mode Banner */}
        {!readOnly && (
          <div className="bg-stone-100 border-b border-stone-200 px-4 py-2 flex items-center gap-2 text-stone-800 text-sm">
            <Pencil className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">Edit Mode: Click on any text or image to edit</span>
          </div>
        )}

        {/* ── Header ── */}
        <header className="bg-gradient-to-r from-stone-900 to-stone-800 text-white px-4 sm:px-6 md:px-8 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
            {/* Left */}
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              {serviceType && (
                <span className="inline-flex items-center self-start px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-white/10 border border-white/20 truncate max-w-full">
                  {serviceType}
                </span>
              )}

              {/* Subtitle */}
              <p className="text-xs sm:text-sm tracking-[0.2em] uppercase text-stone-200 font-medium break-words">
                {!readOnly ? (
                  <EditableText
                    value={localServiceData.subtitle}
                    defaultValue={subtitle}
                    onChange={(v) => {
                      handleInputChange("subtitle", v);
                      void saveFieldToBackend("subtitle", v);
                    }}
                    readOnly={false}
                    as="span"
                    className="text-xs sm:text-sm tracking-[0.2em] uppercase text-stone-200 font-medium"
                    placeholder="Enter subtitle..."
                    uppercase
                  />
                ) : (
                  <span>{(localServiceData.subtitle?.trim() || subtitle).toUpperCase()}</span>
                )}
              </p>

              {/* Title — clamps to avoid overflow */}
              <div className="min-w-0">
                {!readOnly ? (
                  <EditableText
                    value={localServiceData.title || serviceData?.title}
                    defaultValue={title}
                    onChange={(v) => {
                      handleInputChange("title", v);
                      void saveFieldToBackend("title", v);
                    }}
                    readOnly={false}
                    as="h1"
                    className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold leading-tight tracking-tight break-words"
                    placeholder="Enter main title..."
                  />
                ) : (
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold leading-tight tracking-tight break-words line-clamp-3">
                    {localServiceData.title?.trim() || serviceData?.title?.trim() || title}
                  </h1>
                )}
              </div>
            </div>

            {/* Right — tagline */}
            {(tagline || !readOnly) && (
              <div className="sm:text-right min-w-0 max-w-[40%] hidden sm:block">
                <EditableText
                  value={localServiceData.tagline}
                  defaultValue={tagline}
                  onChange={(v) => {
                    handleInputChange("tagline", v);
                    void saveFieldToBackend("tagline", v);
                  }}
                  readOnly={readOnly}
                  as="p"
                  className="text-sm text-stone-200/80 break-words"
                  placeholder="Add a short tagline..."
                />
              </div>
            )}
          </div>
        </header>

        {/* ── Main Grid ── */}
        <main className="grid grid-cols-[515px_320px] gap-0 bg-white min-w-0">

          {/* Left: Hero + About */}
          <section
            className="border-r border-stone-200"
            style={{
              width: "515px",
              minWidth: "515px",
              maxWidth: "515px",
            }}
          >
            {/* Hero Image */}
            <div
              ref={heroContainerRef}
              className="relative w-full bg-stone-200 group overflow-hidden"
              style={{
                width: "515px",
                height: "650px",
                minWidth: "515px",
                maxWidth: "515px",
                minHeight: "650px",
                maxHeight: "650px",
              }}
            >
              {!readOnly && boxHeroRes && (
                <div className="absolute bottom-3 left-3 z-30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-black/80 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    <div className="leading-snug">
                      <div className="font-semibold">
                        Required image resolution: {boxHeroRes}
                      </div>
                      <div className="mt-1 text-[11px]">
                        Please upload exactly this resolution.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <EditableImage
                src={heroImageSrc}
                defaultSrc={photo}
                alt="Hero image"
                onChange={(v) => handleInputChange("heroImage", v)}
                onUploaded={handleHeroImageUpload}
                onLoaded={measureHeroResolution}
                readOnly={readOnly}
                className="object-cover object-center w-full h-full transition-opacity duration-500"
              />
              {!readOnly && heroImageSrc && (
                <div className="absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={openEditImage}
                    className="px-3 py-1.5 bg-white/90 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-white transition-colors text-sm shadow-lg"
                  >
                    <Crop className="w-3 h-3" />
                    Edit
                  </button>
                </div>
              )}
              {!readOnly && (
                <ImageCropModal
                  open={cropModal.open}
                  imageUrl={cropModal.imageUrl}
                  title="Adjust Service Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleEditImageConfirm}
                  onCancel={handleEditImageCancel}
                />
              )}
            </div>

            {/* About */}
            <div className="p-4 sm:p-6 md:p-8">
              <h3 className="font-semibold text-stone-900 mb-3 uppercase tracking-wider text-sm flex items-center gap-2">
                <Star className="h-4 w-4 text-stone-600 flex-shrink-0" />
                <EditableText
                  value={localServiceData.aboutTitle}
                  defaultValue={aboutTitle}
                  onChange={(v) => {
                    handleInputChange("aboutTitle", v);
                    void saveFieldToBackend("aboutTitle", v);
                  }}
                  readOnly={readOnly}
                  as="span"
                  className="inline"
                  uppercase
                />
              </h3>

              <EditableText
                value={localServiceData.description || serviceData?.description}
                defaultValue={description}
                onChange={(v) => {
                  handleInputChange("description", v);
                  void saveFieldToBackend("description", v);
                }}
                readOnly={readOnly}
                as="p"
                multiline
                className="text-stone-600 leading-relaxed text-sm sm:text-base break-words"
                placeholder="Describe your services..."
              />
            </div>
          </section>

          {/* Right: Services List */}
          <section
            className="flex flex-col"
            style={{
              width: "320px",
              minWidth: "320px",
              maxWidth: "320px",
            }}
          >
            <div className="flex-1 p-4 sm:p-6 md:p-8 flex flex-col">
              {/* Header row */}
              <div className="mb-4">
                <h3 className="font-semibold text-stone-900 uppercase tracking-wider text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                    Services
                  </span>
                  {!readOnly && (
                    <span
                      className={`text-xs normal-case font-normal px-2 py-1 rounded-full ml-2 flex-shrink-0 ${features.length >= MAX_FEATURES
                        ? "bg-amber-100 text-amber-700"
                        : "bg-stone-100 text-stone-500"
                        }`}
                    >
                      {features.length}/{MAX_FEATURES}
                    </span>
                  )}
                </h3>
              </div>

              {/* Feature list */}
              <div className="space-y-2 sm:space-y-3 flex-1">
                {features.map((feat, index) => (
                  <div
                    key={feat.id}
                    className="flex items-center min-h-[48px] sm:min-h-[56px] w-full bg-stone-50 border border-stone-200 rounded-lg px-3 sm:px-4 hover:bg-stone-100 hover:border-stone-300 transition-all group"
                  >
                    <ChevronRight className="h-4 w-4 text-stone-400 mr-2 sm:mr-3 flex-shrink-0" />

                    {readOnly ? (
                      <span className="flex-1 text-stone-700 text-sm sm:text-base font-medium break-words leading-snug py-1">
                        {feat.feature_name}
                      </span>
                    ) : (
                      <input
                        type="text"
                        value={feat.feature_name}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        onBlur={() => handleFeatureBlur(index)}
                        className="flex-1 bg-transparent border-none outline-none text-stone-700 text-sm sm:text-base placeholder-stone-400 leading-tight min-w-0"
                        maxLength={30}
                        placeholder="Max 30 Chars"
                      />
                    )}

                    {!readOnly && features.length > 1 && (
                      <button
                        onClick={() => handleFeatureDelete(index)}
                        className="ml-2 text-red-400 hover:text-red-500 text-xl opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                {/* Add button */}
                {!readOnly && features.length < MAX_FEATURES && (
                  <Button
                    onClick={addFeature}
                    variant="outline"
                    className="min-h-[48px] sm:min-h-[56px] w-full border-2 border-dashed border-stone-300 text-stone-600 bg-stone-50/50 hover:border-stone-400 hover:text-stone-700 hover:bg-stone-100/50 rounded-lg text-sm sm:text-base font-medium transition-all"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Add Service
                  </Button>
                )}

                {!readOnly && features.length >= MAX_FEATURES && (
                  <p className="text-center text-sm text-stone-400 italic py-2">
                    Maximum of {MAX_FEATURES} services reached
                  </p>
                )}

                {features.length === 0 && (
                  <div className="text-center py-8 bg-stone-50 rounded-xl border-2 border-dashed border-stone-200">
                    <Star className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                    <p className="text-stone-400 text-sm">
                      {readOnly ? "No services added" : "Click 'Add Service' to add your first service"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>

        {/* ── Footer ── */}
        <footer className="bg-gradient-to-r from-stone-900 to-stone-800 text-white">
          <div className="w-full px-4 sm:px-3 py-3 sm:py-5">
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 sm:gap-5">

              {/* Avatar */}
              <EditableImage
                src={displayAvatar}
                defaultSrc={imageOne}
                alt="Profile Picture"
                onChange={(v) => handleInputChange("profileImage", v)}
                readOnly={true}   // ← change from readOnly={readOnly}
                className="w-[150px] h-[180px] rounded-xl overflow-hidden shadow-lg flex-shrink-0"
                fallbackIcon={<User className="w-6 h-6 sm:w-8 sm:h-8 text-stone-400" />}
              />

              {/* Contact details */}
              <div className="flex flex-col items-center gap-1 leading-tight min-w-0 text-center">

                {/* Name */}
                <p className="text-base sm:text-lg md:text-xl font-semibold text-white truncate">{agentFullName}</p>

                {/* Phone */}
                <div className="inline-flex items-center gap-1.5 mx-auto">
                  <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-stone-400 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-stone-300">{displayPhone}</span>
                </div>

                {/* Email */}
                <div className="inline-flex items-center gap-1.5 mx-auto">
                  <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-stone-400 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-stone-300 underline">
                    {/* {displayEmail} */}
                     Click here to email Me
                    </span>
                   
                </div>

                {/* Company */}
                {displayCompanyName && (
                  <div className="inline-flex items-center gap-1.5 mx-auto mt-0.5">
                    <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-stone-400 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-stone-300">{displayCompanyName}</span>
                  </div>
                )}

                {/* License Number */}
                {userData?.license_number && (
                  <div className="inline-flex items-center gap-1.5 mx-auto mt-0.5">
                    <IdCard className="h-3 w-3 sm:h-4 sm:w-4 text-stone-400 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-stone-300">
                      License #: {userData.license_number}
                    </span>
                  </div>
                )}

              </div>

              {/* Company Logo */}
              <EditableImage
                src={displayCompanyLogo}
                defaultSrc={imageTwo}
                alt="Company Logo"
                onChange={(v) => handleInputChange("companyImage", v)}
                readOnly={true}
                className="w-[180px] h-[180px] rounded-xl overflow-hidden shadow-lg flex-shrink-0"
                backgroundClassName="bg-transparent"
                fallbackIcon={<Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-stone-400" />}
              />

            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ServiceTemplate2;
